import useSwal from "@common-hooks/useSwal"
import { useAuth } from "../../../../AppContext"
import React from "react"
import { IConsultantDocumentModel, IConsultantMissionModel, IConsultantModel } from "@common-models/*"
import { toast } from "react-toastify"
import {
     addConsultantDocumentRequest,
     addVersionSigneeConsultantDocument,
     addVersionSigneeConsultantMissionDoc,
     deleteConsultantDocument,
     replaceConsultantDocument,
     replaceConsultantMissionDoc,
} from "./_requests"
import { AxiosError } from "axios"
import { CONST_CIVILITE_MME } from "@common-constants/*"
import { disableEspaceConsultant, enableEspaceConsultant, envoyerInstructionsConnexionEspaceCollabRequest } from "../details/core/_requests"
import Swal from "sweetalert2"
import { envoyerDocsEmbauche, ignorerEnvoiDocsEmbaucheDocuments } from "../../../recrutement/demandesEmbauche/details/_documents/core/_requests"

export const useConsultant = () => {
     const { swal } = useSwal()
     const { currentUser } = useAuth()

     /* BEGIN: document de manière générale sauf ODM mission */
     function documentAdd(consultant_ID: number, e: React.ChangeEvent<HTMLInputElement>, type: string, callback: (val: IConsultantDocumentModel) => void = () => {}) {
          const fileBinary = e.target.files![0]

          if (fileBinary) {
               e.target.disabled = true
               toast.info(`Envoi du document en cours ...`, { autoClose: false })

               addConsultantDocumentRequest(consultant_ID, type, fileBinary)
                    .then(r => {
                         callback(r.data)
                         toast.dismiss()
                         toast.success("Le document a bien ajouté.")
                    })
                    .catch((e: AxiosError) => {
                         toast.dismiss()
                         toast.error(e?.response?.data?.detail, { autoClose: false })
                    })
                    .finally(() => {
                         e.target.value = ""
                         e.target.disabled = false
                    })
          }
     }

     function documentFlagAsDeleted(id: number, callback: () => void = () => {}) {
          swal.fire({
               icon: "warning",
               text: `Êtes-vous sûr${currentUser?.civilite === CONST_CIVILITE_MME ? "e" : ""} de vouloir supprimer le document?`,
               showCancelButton: true,
               confirmButtonText: "Oui",
               cancelButtonText: "Annuler",
               focusConfirm: true,
               showLoaderOnConfirm: true,
               preConfirm: async () => {
                    return deleteConsultantDocument(id)
                         .then(() => {
                              callback()
                              toast.success("Le document a bien été supprimé.")
                         })
                         .catch(e => {
                              toast.error(e?.response?.data?.detail, { autoClose: false })
                         })
               },
               allowOutsideClick: () => !swal.isLoading(),
          }).then()
     }

     function documentReplace(e: React.ChangeEvent<HTMLInputElement>, id: number, callback: (val: IConsultantDocumentModel) => void = () => {}) {
          const fileBinary = e.target.files![0]

          if (fileBinary) {
               e.target.disabled = true
               toast.info("Remplacement du document en cours ...", { autoClose: false })

               replaceConsultantDocument(id, fileBinary)
                    .then(r => {
                         callback(r.data)
                         toast.dismiss()
                         toast.success("Le document a bien été remplacé.")
                    })
                    .catch(e => {
                         toast.dismiss()
                         toast.error(e?.response?.data?.detail, { autoClose: false })
                    })
                    .finally(() => {
                         e.target.value = ""
                         e.target.disabled = false
                    })
          }
     }

     function documentAddVersionSignee(e: React.ChangeEvent<HTMLInputElement>, id: number, callback: (val: IConsultantDocumentModel) => void = () => {}) {
          const fileBinary = e.target.files![0]

          if (fileBinary) {
               e.target.disabled = true
               toast.info("Ajout de la version signée en cours ...", { autoClose: false })

               addVersionSigneeConsultantDocument(id, fileBinary)
                    .then(r => {
                         callback(r.data)
                         toast.dismiss()
                         toast.success("Le version signée a bien été ajoutée.")
                    })
                    .catch(e => {
                         toast.dismiss()
                         toast.error(e?.response?.data?.detail, { autoClose: false })
                    })
                    .finally(() => {
                         e.target.value = ""
                         e.target.disabled = false
                    })
          }
     }

     /* END: document de manière générale sauf ODM mission */

     /* BEGIN: ODM mission */
     function ordreDeMissionDocumentReplace(e: React.ChangeEvent<HTMLInputElement>, id: number, callback: (val: IConsultantMissionModel) => void = () => {}) {
          const fileBinary = e.target.files![0]

          if (fileBinary) {
               e.target.disabled = true
               toast.info("Remplacement du document en cours ...", { autoClose: false })

               replaceConsultantMissionDoc(id, fileBinary)
                    .then(r => {
                         callback(r.data)
                         toast.dismiss()
                         toast.success("Le document a bien été remplacé.")
                    })
                    .catch(e => {
                         toast.dismiss()
                         toast.error(e?.response?.data?.detail, { autoClose: false })
                    })
                    .finally(() => {
                         e.target.value = ""
                         e.target.disabled = false
                    })
          }
     }

     function ordreDeMissionAddVersionSignee(e: React.ChangeEvent<HTMLInputElement>, id: number, callback: (val: IConsultantMissionModel) => void = () => {}) {
          const fileBinary = e.target.files![0]

          if (fileBinary) {
               e.target.disabled = true
               toast.info("Ajout de la version signée en cours ...", { autoClose: false })

               addVersionSigneeConsultantMissionDoc(id, fileBinary)
                    .then(r => {
                         callback(r.data)
                         toast.dismiss()
                         toast.success("Le version signée a bien été ajoutée.")
                    })
                    .catch(e => {
                         toast.dismiss()
                         toast.error(e?.response?.data?.detail, { autoClose: false })
                    })
                    .finally(() => {
                         e.target.value = ""
                         e.target.disabled = false
                    })
          }
     }

     /* END: ODM mission */

     /* BEGIN: ACCOUNT ACTIONS */
     function accountEnableDisableEspaceConsultant(
          consultant: IConsultantModel,
          onConfirmCallback?: () => void,
          onSuccessCallback?: () => void,
          onDismissCallback?: () => void,
          onErrorCallback?: () => void
     ) {
          swal.fire({
               icon: "warning",
               title:
                    consultant.candidat?.user?.roles?.includes("ROLE_CONSULTANT") && consultant.candidat?.user.enabled
                         ? "Voulez-vous couper son accès à l'espace Talent?"
                         : "Voulez-vous lui donner accès à son espace collaborateur ?",
               html: "Aucune notification lui sera envoyée.",
               showCancelButton: true,
               confirmButtonText: "Oui",
               cancelButtonText: "Annuler",
               showLoaderOnConfirm: true,
               preConfirm: async () => {
                    const hasAccess = !!(consultant.candidat?.user?.roles && consultant.candidat.user.roles.includes("ROLE_CONSULTANT") && consultant.candidat?.user.enabled)
                    const request = hasAccess ? disableEspaceConsultant : enableEspaceConsultant

                    if (onConfirmCallback) onConfirmCallback()

                    return request(consultant.id as number)
                         .then(() => {
                              if (onSuccessCallback) onSuccessCallback()

                              /*toast.success(
                                   hasAccess ? "Le collaborateur n'aura plus accès à son espace consultant." : "Le collaborateur a désormais accès à son espace consultant."
                              )
                              queryClient.setQueryData<IGetDetailsConsultant | undefined>(REACT_QUERY_KEY_GET_CONSULTANT_DETAILS, (prev: IGetDetailsConsultant | undefined) => {
                                   if (prev) {
                                        prev.consultant = {
                                             ...prev.consultant,
                                             candidat: {
                                                  ...prev.consultant.candidat,
                                                  user: {
                                                       ...prev.consultant.candidat?.user,
                                                       enabled: !prev.consultant.candidat?.user?.roles?.includes("ROLE_CONSULTANT"),
                                                       roles: prev.consultant.candidat?.user?.roles?.includes("ROLE_CONSULTANT")
                                                            ? ["ROLE_CANDIDAT_EN_COURS_EMBAUCHE"]
                                                            : ["ROLE_CONSULTANT"],
                                                  },
                                             },
                                        }
                                   }

                                   return prev
                              })*/
                         })
                         .catch(e => {
                              if (onErrorCallback) onErrorCallback()
                              toast.error(e?.response?.data?.detail, { autoClose: false })
                         })
               },
               allowOutsideClick: () => !Swal.isLoading(),
          }).then(result => {
               if (result.isDismissed && onDismissCallback) onDismissCallback()
          })
     }

     function accountEnvoyerInstructionsConnexion(
          consultant: IConsultantModel,
          onConfirmCallback?: () => void,
          onSuccessCallback?: () => void,
          onDismissCallback?: () => void,
          onErrorCallback?: () => void
     ) {
          swal.fire({
               icon: "warning",
               title: "Voulez-vous envoyer les instructions de connexion à l'espace Talent?",
               showCancelButton: true,
               confirmButtonText: "Oui",
               cancelButtonText: "Annuler",
               showLoaderOnConfirm: true,
               preConfirm: async () => {
                    if (onConfirmCallback) onConfirmCallback()

                    return envoyerInstructionsConnexionEspaceCollabRequest(consultant.id as number)
                         .then(() => {
                              if (onSuccessCallback) onSuccessCallback()

                              // toast.success("Instructions envoyées avec succès.")
                         })
                         .catch(e => {
                              if (onErrorCallback) onErrorCallback()
                              toast.error(e?.response?.data?.detail, { autoClose: false })
                         })
               },
               allowOutsideClick: () => !Swal.isLoading(),
          }).then(result => {
               if (result.isDismissed && onDismissCallback) onDismissCallback()
          })
     }

     /* END: ACCOUNT ACTIONS */

     /* BEGIN: DOCS EMBAUCHE */
     function documentsEmbaucheIgnorerEnvoi(
          consultant: IConsultantModel,
          onConfirmCallback?: () => void,
          onSuccessCallback?: () => void,
          onDismissCallback?: () => void,
          onErrorCallback?: () => void
     ) {
          swal.fire({
               icon: "warning",
               title: "Vous êtes sur le point d'ignorer l'envoi des documents d'embauche",
               text: "Voulez-vous procéder ?",
               showCancelButton: true,
               confirmButtonText: "Confirmer",
               cancelButtonText: "Annuler",
               showLoaderOnConfirm: true,
               preConfirm: async () => {
                    if (onConfirmCallback) onConfirmCallback()

                    return ignorerEnvoiDocsEmbaucheDocuments(consultant.id!)
                         .then(() => {
                              if (onSuccessCallback) onSuccessCallback()
                         })
                         .catch(e => {
                              if (onErrorCallback) onErrorCallback()
                              toast.error(e?.response?.data?.detail, { autoClose: false })
                         })
               },
               allowOutsideClick: () => !Swal.isLoading(),
          }).then(result => {
               if (result.isDismissed && onDismissCallback) onDismissCallback()
          })
     }

     function documentsEmbaucheEnvoyer(
          consultant: IConsultantModel,
          isContratTravailIncluded: boolean,
          isOdmIncluded: boolean,
          onConfirmCallback?: () => void,
          onSuccessCallback?: () => void,
          onDismissCallback?: () => void,
          onErrorCallback?: () => void
     ) {
          swal.fire({
               icon: "warning",
               title: "Vous êtes sur le point d'envoyer les documents d'embauche",
               text: "Voulez-vous procéder ?",
               showCancelButton: true,
               confirmButtonText: "Confirmer",
               cancelButtonText: "Annuler",
               showLoaderOnConfirm: true,
               preConfirm: async () => {
                    if (onConfirmCallback) onConfirmCallback()

                    return envoyerDocsEmbauche(consultant.id!, isContratTravailIncluded, isOdmIncluded)
                         .then(() => {
                              if (onSuccessCallback) onSuccessCallback()
                         })
                         .catch(e => {
                              if (onErrorCallback) onErrorCallback()
                              toast.error(e?.response?.data?.detail, { autoClose: false })
                         })
               },
               allowOutsideClick: () => !Swal.isLoading(),
          }).then(result => {
               if (result.isDismissed && onDismissCallback) onDismissCallback()
          })
     }

     /* END: DOCS EMBAUCHE */

     return {
          accountEnableDisableEspaceConsultant,
          accountEnvoyerInstructionsConnexion,
          documentAdd,
          documentFlagAsDeleted,
          documentReplace,
          documentAddVersionSignee,
          ordreDeMissionDocumentReplace,
          ordreDeMissionAddVersionSignee,
          documentsEmbaucheEnvoyer,
          documentsEmbaucheIgnorerEnvoi,
     }
}
